import { AppointmentProductPaymentDetailsRequest } from '@/api/requests/appointments/appointment-product-payment-details-request';
import { Appointment } from '@/shared/appointments/appointment';
import { AppointmentProductPaymentDetails } from '@/shared/appointments/appointment-product-payment-details';
import { InstructionType } from '@/shared/instruction-type';
import { LocationType } from '@/shared/location-type';
import { RoleName } from '@/shared/role-name';
import { AppointmentCancellationType } from '@/shared/types/appointment-cancellation-type';
import { AppointmentExecutorType } from '@/shared/types/appointment-executor-type';
import { AppointmentNoInstructionType } from '@/shared/types/appointment-no-instruction-type';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { AppointmentProductType } from '@/shared/types/appointment-product-type';
import { AppointmentReasonType } from '@/shared/types/appointment-reason-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { User } from '@/shared/user';
import store from '@/store';
import { DateTime } from 'luxon';

export function appointmentTypeDescription(appointmentTypeId: string | null): string {
    if (appointmentTypeId == null) {
        return '';
    }

    const appointmentTypes = store.getters['types/appointmentTypes'] as Array<AppointmentType>;
    const appointmentType = appointmentTypes.find((type) => type.id === appointmentTypeId);

    return appointmentType == null ? '' : appointmentType.value;
}

export function locationTypeDescription(locationType: LocationType): string {
    if (locationType == null) {
        return '';
    }

    switch (locationType) {
        case LocationType.Office:
            return 'Office';
        case LocationType.Home:
            return 'Home';
        case LocationType.Telephone:
            return 'Telephone';
        case LocationType.Video:
            return 'Video';
        default:
            return '';
    }
}

export function reasonTypeDescription(reasonTypeId: string | null): string {
    if (reasonTypeId == null) {
        return '';
    }

    const reasonTypes = store.getters['types/appointmentReasonTypes'] as Array<AppointmentReasonType>;
    const reasonType = reasonTypes.find((type) => type.id === reasonTypeId);

    return reasonType == null ? '' : reasonType.value;
}

export function outcomeTypeDescription(outcomeTypeId: string | null): string {
    if (outcomeTypeId == null) {
        return '';
    }

    const outcomeTypes = store.getters['types/appointmentOutcomeTypes'] as Array<AppointmentOutcomeType>;
    const outcomeType = outcomeTypes.find((type) => type.id === outcomeTypeId);

    return outcomeType == null ? '' : outcomeType.value;
}

export function instructionTypeDescription(instructionType: InstructionType): string {
    if (instructionType == null) {
        return '';
    }

    switch (instructionType) {
        case InstructionType.NotInstructed:
            return 'Not Instructed';
        case InstructionType.CoolingOff:
            return '14 Day Cooling Off Period';
        case InstructionType.ConsideringOptions:
            return 'Considering Options';
        case InstructionType.Instructed:
            return 'Instructed';
        default:
            return '';
    }
}

export function executorTypeDescription(executorTypeId: string | null): string {
    if (executorTypeId == null) {
        return '';
    }

    const executorTypes = store.getters['types/appointmentExecutorTypes'] as Array<AppointmentExecutorType>;
    const executorType = executorTypes.find((type) => type.id === executorTypeId);

    return executorType == null ? '' : executorType.value;
}

export function cancellationTypeDescription(cancellationTypeId: string | null): string {
    if (cancellationTypeId == null) {
        return '';
    }

    const cancellationTypes = store.getters['types/appointmentCancellationTypes'] as Array<AppointmentCancellationType>;
    const cancellationType = cancellationTypes.find((type) => type.id === cancellationTypeId);

    return cancellationType == null ? '' : cancellationType.value;
}

export function noInstructionTypeDescription(noInstructionTypeId: string | null): string {
    if (noInstructionTypeId == null) {
        return '';
    }

    const noInstructionTypes = store.getters['types/appointmentNoInstructionTypes'] as Array<AppointmentNoInstructionType>;
    const noInstructionType = noInstructionTypes.find((type) => type.id === noInstructionTypeId);

    return noInstructionType == null ? '' : noInstructionType.value;
}

export function appointmentProductTypeDescription(productTypeId: string | null): string {
    if (productTypeId == null) {
        return '';
    }

    const appointmentProductTypes = store.getters['types/appointmentProductTypes'] as Array<AppointmentProductType>;
    const appointmentProductType = appointmentProductTypes.find((type) => type.id === productTypeId);

    return appointmentProductType == null ? '' : appointmentProductType.value;
}

export function outcomePaymentStatusTypeDescription(paymentStatusTypeId: string | null): string {
    if (paymentStatusTypeId == null) {
        return '';
    }

    const paymentStatusTypes = store.getters['types/appointmentPaymentStatusTypes'] as Array<AppointmentPaymentStatusType>;
    const paymentStatusType = paymentStatusTypes.find((type) => type.id === paymentStatusTypeId);

    return paymentStatusType == null ? '' : paymentStatusType.value;
}

export function productPaymentStatusTypeDescription(paymentDetails: Array<AppointmentProductPaymentDetails> | Array<AppointmentProductPaymentDetailsRequest> | null): string {
    if (paymentDetails == null || !paymentDetails.length) {
        return '';
    }

    const paymentStatusTypes = store.getters['types/appointmentPaymentStatusTypes'] as Array<AppointmentPaymentStatusType>;

    if (paymentDetails.length > 1) {
        for (const payment of paymentDetails) {
            const paymentStatusType = paymentStatusTypes.find((type) => type.id === payment.paymentStatusId);

            if (paymentStatusType != null && paymentStatusType.isComplete) {
                continue;
            }

            return 'Part Payment';
        }

        return 'Cleared';
    }

    const paymentStatus = paymentStatusTypes.find((type) => type.id === paymentDetails[0].paymentStatusId);

    return paymentStatus == null ? '' : paymentStatus.value;
}

export function canEditAppointmentOutcome(appointment: Appointment): boolean {
    if (appointment.id == null || appointment.isRebooked || appointment.outcome == null) {
        return false;
    }

    if (appointment.outcome.outcomeTypeId == null) {
        return false;
    }

    const outcomeTypes = store.getters['types/appointmentOutcomeTypes'] as Array<AppointmentOutcomeType>;
    const outcomeType = outcomeTypes.find((type) => type.id === appointment.outcome!.outcomeTypeId!);

    if (outcomeType == null) {
        return false;
    }

    const user = store.getters['user/user'] as User;

    if (user.roles.some((role) => role.name.toLowerCase() === RoleName.Administrator.toLowerCase() || role.name.toLowerCase() === RoleName.InternalKeyStakeholder.toLowerCase())) {
        return true;
    }

    if (!outcomeType.isSat) {
        return false;
    }

    if (appointment.outcome.isLegacy) {
        if (appointment.outcome.instructionType == null || appointment.outcome.instructionType === InstructionType.NotInstructed) {
            return false;
        }

        if (appointment.outcome.instructionType === InstructionType.CoolingOff || appointment.outcome.instructionType === InstructionType.ConsideringOptions) {
            if (new Date(appointment.date!.toString()) < DateTime.fromJSDate(new Date()).minus({ months: 6 }).toJSDate()) {
                return false;
            }

            return true;
        }

        if (appointment.outcome.paymentDetails == null) {
            return true;
        }

        if (appointment.outcome.paymentDetails.paymentStatusId == null) {
            return true;
        }

        const paymentStatusTypes = store.getters['types/activeAppointmentPaymentStatusTypes'] as Array<AppointmentPaymentStatusType>;
        const paymentStatusType = paymentStatusTypes.find((type) => type.id === appointment.outcome!.paymentDetails!.paymentStatusId!);

        if (paymentStatusType == null) {
            return false;
        }

        return !paymentStatusType.isComplete;
    } else {
        if (appointment.outcome.products == null || !appointment.outcome.products.length) {
            return false;
        }

        if (appointment.outcome.products.every((product) => product.instructionType === InstructionType.NotInstructed)) {
            return false;
        }

        const validFrom = DateTime.fromJSDate(new Date()).minus({ months: 6 }).toJSDate();

        if (appointment.outcome.products.some((product) => (product.instructionType === InstructionType.CoolingOff || product.instructionType === InstructionType.ConsideringOptions) && new Date(appointment.date!.toString()) > validFrom)) {
            return true;
        }

        const instructedProducts = appointment.outcome.products.filter((product) => product.instructionType === InstructionType.Instructed);

        if (instructedProducts == null || !instructedProducts.length) {
            return false;
        }

        const paymentStatusTypes = store.getters['types/activeAppointmentPaymentStatusTypes'] as Array<AppointmentPaymentStatusType>;

        for (const product of instructedProducts) {
            if (product.paymentDetails == null || !product.paymentDetails.length) {
                return true;
            }

            for (const payment of product.paymentDetails) {
                if (payment.paymentStatusId == null) {
                    return true;
                }

                const paymentStatusType = paymentStatusTypes.find((type) => type.id === payment.paymentStatusId);

                if (paymentStatusType == null) {
                    continue;
                }

                if (!paymentStatusType.isComplete) {
                    return true;
                }
            }
        }

        return false;
    }
}
